/*
 * Copyright 2026, Airspan Networks
 * All Rights Reserved.
 */

import { useState, useMemo } from "react";
import { API } from "../../../globals/globals";
import {
  useGetInventoryRadioNodesQuery,
  useGetInventoryCellsQuery,
  useExportInventoryMutation,
  useGetDrawerCountsMutation,
  useGetAlarmsByDeviceKeyMutation,
} from "../../../store/api/devices/deviceApiSlice";
import { useSelector } from "react-redux";
import { InventoryRadioNodeColumns, InventoryCellColumns } from "../../../components/Shared/Table/Columns";
import { searchKeywordByScopeSelector } from "../../../store/search/selector";
import { setIsLoadingStatus, setOpenToast } from "../../../store/helpers/helperReducers";
import {
  setOpenDeviceDrawer,
  setDrawerCounts,
  setSelectedDevice,
  setSelectedDeviceAlarms,
  setSelectedDeviceAlarmCount,
} from "../../../store/api/devices/reducers";
import { useGetDeviceFullDetailMutation } from "../../../store/api/deviceConfig/deviceConfigApiSlice";
import { IRadioNodeInventory, ICellInventory } from "../../../store/api/devices/types";
import { GridColDef } from "@mui/x-data-grid-pro";
import { Icon } from "../../../components/Shared/Icon";
import { useStoreDispatch } from "../../../store/hooks";
import { paginationModelSelector } from "../../../store/helpers/helperSelectors";
import { errorMessage, exportInCSV } from "../../../utils/helpers";
import { initialState } from "../../../store/helpers/helperInitialState";
import {
  inventoryRadioNodesUrlParameterSelector,
  inventoryCellsUrlParameterSelector,
  inventoryViewSelector,
} from "../../../store/filters/Inventory/selectors";

const RADIO_NODE_SORT_FIELD_MAP: Record<string, string> = {
  radioNodeName: "HOSTNAME",
  index: "EQUIPMENTINDEX",
  geoRegion: "GEOREGION",
  description: "REMARKS",
  state: "STATUS",
  model: "MODEL",
  hostname: "PARENTHOSTNAME",
  serialNumber: "SERIALNUMBER",
};

const CELL_SORT_FIELD_MAP: Record<string, string> = {
  cell: "HOSTNAME",
  cellId: "EQUIPMENTINDEX",
  technology: "EQUIPMENTTYPE",
  description: "REMARKS",
  radioNode: "OBJECTKEY",
  hostname: "PARENTHOSTNAME",
};

export const useInventoryData = () => {
  const dispatch = useStoreDispatch();
  const view = useSelector(inventoryViewSelector);
  const radioNodeFilters = useSelector(inventoryRadioNodesUrlParameterSelector);
  const cellFilters = useSelector(inventoryCellsUrlParameterSelector);
  const keyword = useSelector(searchKeywordByScopeSelector("inventory"));
  const { inventory = initialState.pagination } = useSelector(paginationModelSelector);
  const [sortOrder, setSortOrder] = useState("HOSTNAME,asc");

  const setInventoryTableSort = (sortString: string) => {
    const commaIdx = sortString.lastIndexOf(",");
    if (commaIdx === -1) { setSortOrder(sortString); return; }
    const field = sortString.substring(0, commaIdx);
    const dir = sortString.substring(commaIdx + 1);
    const backendField = view === "radionodes"
      ? (RADIO_NODE_SORT_FIELD_MAP[field] ?? field)
      : (CELL_SORT_FIELD_MAP[field] ?? field);
    setSortOrder(`${backendField},${dir}`);
  };

  const activeFilters = new URLSearchParams(view === "cells" ? cellFilters : radioNodeFilters);
  activeFilters.set("sort", sortOrder);
  activeFilters.set("page", inventory.page.toString());
  activeFilters.set("size", inventory.pageSize.toString());
  activeFilters.set("search", keyword);

  const filtersString = activeFilters.toString();
  const args = { filters: filtersString };

  const { data: radioNodeData, isLoading: radioNodeLoading } = useGetInventoryRadioNodesQuery(args, {
    pollingInterval: API.POLLING_INTERVAL,
    skip: view !== "radionodes",
  });

  const { data: cellData, isLoading: cellLoading } = useGetInventoryCellsQuery(args, {
    pollingInterval: API.POLLING_INTERVAL,
    skip: view !== "cells",
  });

  const data = view === "cells" ? (cellData?.content || []) : (radioNodeData?.content || []);
  const totalItems = view === "cells" ? (cellData?.totalElements || 0) : (radioNodeData?.totalElements || 0);
  const isLoading = view === "cells" ? cellLoading : radioNodeLoading;
  const InventoryColumns = view === "cells" ? InventoryCellColumns : InventoryRadioNodeColumns;

  const [getDrawerCounts] = useGetDrawerCountsMutation();
  const [getDeviceFullDetail] = useGetDeviceFullDetailMutation();
  const [getAlarmsByDeviceKey] = useGetAlarmsByDeviceKeyMutation();

  const openDeviceDrawerHandler = async (row: IRadioNodeInventory | ICellInventory) => {
    let objectKey = row.hostname || (row as IRadioNodeInventory).serialNumber;
    if (objectKey && objectKey.includes(':')) {
      objectKey = objectKey.split(':')[0];
    }
    if (!objectKey) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: "No device identifier found" }));
      return;
    }
    dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: true, isLoadingAlarms: true }));
    dispatch(setOpenDeviceDrawer(true));
    try {
      const args = { filters: `key=objectKey&value=${objectKey}&weightType=FULL`, objectKey };
      const [drawerCountsResponse, deviceDetailResponse, alarmResponse] = await Promise.all([
        getDrawerCounts({ deviceKey: objectKey, filters: "" }),
        getDeviceFullDetail(args),
        getAlarmsByDeviceKey({ deviceKey: objectKey, filters: "?sort=retention%2Cdesc&page=0&size=50" }),
      ]);
      if ("error" in drawerCountsResponse || "error" in deviceDetailResponse || "error" in alarmResponse) {
        let error: any = "";
        if ("error" in drawerCountsResponse) error = drawerCountsResponse.error;
        if ("error" in deviceDetailResponse) error = deviceDetailResponse.error;
        if ("error" in alarmResponse) error = alarmResponse.error;
        throw error;
      }
      const { data: configCount } = drawerCountsResponse;
      const { data: deviceData } = deviceDetailResponse;
      const { data: alarmData } = alarmResponse;
      if (!!configCount && !!deviceData && deviceData.data[0] && alarmData) {
        dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
        dispatch(setDrawerCounts(configCount));
        dispatch(setSelectedDevice({ ...deviceData.data[0], ...deviceData?.deviceParams }));
        dispatch(setSelectedDeviceAlarms(alarmData.data));
        dispatch(setSelectedDeviceAlarmCount(alarmData.totalItems || 0));
      }
    } catch (error: any) {
      dispatch(setIsLoadingStatus({ isLoadingDeviceDetail: false, isLoadingAlarms: false }));
      dispatch(setOpenDeviceDrawer(false));
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const customInventoryColumns = useMemo<GridColDef[]>(() => {
    return InventoryColumns.map((col) => {
      if (col.field === "hostname") {
        return {
          ...col,
          renderCell: (cellValues: any) => {
            const displayValue = cellValues.row.parenthostname || '';
            return (
              <span
                onClick={() => openDeviceDrawerHandler(cellValues.row)}
                className="group/devlink inline-flex items-center gap-1 min-w-0 text-link cursor-pointer"
              >
                <span className="font-medium truncate group-hover/devlink:underline">{displayValue}</span>
                <Icon
                  name="open_in_new"
                  size={14}
                  className="shrink-0 opacity-0 group-hover/devlink:opacity-70 transition-opacity text-muted-foreground"
                />
              </span>
            );
          },
        };
      }
      return col;
    });
  }, [InventoryColumns, openDeviceDrawerHandler]);

  const [exportInventory, exportStatus] = useExportInventoryMutation();

  const exportHandler = async () => {
    try {
      const filterParam = activeFilters.get("filter") || undefined;
      const response: any = await exportInventory({
        type: view,
        filters: filterParam,
        sort: sortOrder,
        search: keyword || undefined,
      });
      if (response?.error) throw response?.error;
      const viewLabel = view === "cells" ? "Cell" : "RadioNode";
      exportInCSV(response, `Inventory-${viewLabel}`);
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  return {
    data,
    totalItems,
    isLoading,
    InventoryColumns: customInventoryColumns,
    exportHandler,
    exportStatus,
    setInventoryTableSort,
  };
};
